import { useState } from "react";

import { useToast } from "@chakra-ui/react";
import { useLoading } from "src/Manager/hooks/form";
import { ScheduledUpdateValues } from "../EditScheduledUpdateModal";
import actions from "src/actions";
import { useCallback } from "react";
import { Performer } from "../../Lineup/LineupSection";
import { KYDEventScheduledUpdateSummary } from "@common/interfaces/KYDEvent";
import { BodyDoc } from "@common/interfaces/KYDCampaign";

const useScheduledUpdates = () => {
  const [loading, updateLoading] = useLoading({
    scheduledUpdate: false,
  });
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false);
  const [editingScheduledUpdate, setEditingScheduledUpdate] =
    useState<KYDEventScheduledUpdateSummary | null>(null);
  const [editingField, setEditingField] = useState<
    | "name"
    | "subtitle"
    | "description"
    | "description_rich"
    | "date"
    | "poster"
    | "performers"
    | null
  >(null);
  const toast = useToast();

  const onUpdateScheduledUpdate = async (
    eventId: string,
    scheduledUpdate: ScheduledUpdateValues,
    {
      onSuccess = async () => {},
    }: {
      onSuccess?: () => Promise<void>;
    }
  ) => {
    console.log(scheduledUpdate);
    try {
      updateLoading("scheduledUpdate", true);
      await actions.ADMIN.updateScheduledUpdate(eventId, [
        {
          id: scheduledUpdate.id,
          name: scheduledUpdate.name,
          subtitle: scheduledUpdate.subtitle,
          description: scheduledUpdate.description,
          description_rich: scheduledUpdate.description_rich as BodyDoc,
          date: scheduledUpdate.date,
          poster: scheduledUpdate.poster,
          performers: scheduledUpdate.new_performers.map((performer) => ({
            id: performer.id,
            name: performer.name,
            image_url: performer.image_url,
            set_time: performer.set_time,
            headliner: false,
          })),
        },
      ]);
      await onSuccess();
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: err.message,
        status: "error",
      });
    } finally {
      updateLoading("scheduledUpdate", false);
    }
  };

  const onDeleteScheduledUpdate = async (
    eventId: string,
    scheduledUpdate: KYDEventScheduledUpdateSummary,
    //type: "description" | "poster" | "performers" | "name",
    {
      onSuccess = async () => {},
    }: {
      onSuccess?: () => Promise<void>;
    } = {
      onSuccess: async () => {},
    }
  ) => {
    try {
      updateLoading("scheduledUpdate", true);
      await actions.ADMIN.deleteScheduledUpdate(
        eventId,
        scheduledUpdate.date
        //type
      );
      await onSuccess();
    } catch (err) {
      console.error(err);
    } finally {
      updateLoading("scheduledUpdate", false);
    }
  };

  const onAddPerformer = useCallback(
    async ({
      eventId,
      name,
      soundcharts_id,
      profiles,
      image_url,
    }: {
      eventId: string;
      name: string;
      soundcharts_id: string;
      profiles: {
        platform: string;
        url: string;
      }[];
      image_url: string;
    }) => {
      try {
        const performer = await actions.ADMIN.createPerformer(eventId, {
          soundcharts_id,
          profiles,
          name,
          image_url,
        });

        return performer;
      } catch (err) {
        console.error(err);
      }
    },
    []
  );

  const updatePerformers = useCallback(
    async (eventId: string, performers: Performer[]) => {
      for await (const performer of performers) {
        await onAddPerformer({
          eventId,
          name: performer.name,
          soundcharts_id: performer.soundcharts_id,
          profiles: performer.profiles,
          image_url: performer.image_url,
        });
      }
    },
    [onAddPerformer]
  );

  const onCreateScheduledUpdate = async (
    eventId: string,
    scheduledUpdate: ScheduledUpdateValues,
    {
      justPerformers = false,
      onSuccess,
    }: {
      justPerformers?: boolean;
      onSuccess: () => Promise<void>;
    } = {
      onSuccess: async () => {},
    }
  ) => {
    try {
      updateLoading("scheduledUpdate", true);

      if (justPerformers) {
        const newPerformers = scheduledUpdate.new_performers.filter(
          (performer) => performer.isNew
        );
        await updatePerformers(eventId, newPerformers);
        await onSuccess();
        return;
      }

      await actions.ADMIN.createScheduledUpdate(eventId, [
        {
          id: scheduledUpdate.id,
          name: scheduledUpdate.name,
          subtitle: scheduledUpdate.subtitle,
          description: scheduledUpdate.description,
          description_rich: scheduledUpdate.description_rich as BodyDoc,
          date: scheduledUpdate.date,
          poster: scheduledUpdate.poster,
          performers: scheduledUpdate.new_performers.map((performer) => ({
            id: performer.id,
            name: performer.name,
            image_url: performer.image_url,
            headliner: false,
            set_time: performer.set_time,
          })),
        },
      ]);
      await onSuccess();
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: err.message,
        status: "error",
      });
    } finally {
      updateLoading("scheduledUpdate", false);
    }
  };
  const getScheduledUpdateForField = useCallback(
    (
      scheduledUpdates: KYDEventScheduledUpdateSummary[],
      field:
        | "name"
        | "description"
        | "description_rich"
        | "date"
        | "poster"
        | "performers"
    ) => {
      return scheduledUpdates.find((update) => update[field]);
    },
    []
  );

  const hasScheduledUpdatesForField = useCallback(
    (
      scheduledUpdates: KYDEventScheduledUpdateSummary[],
      field:
        | "name"
        | "description"
        | "description_rich"
        | "date"
        | "poster"
        | "performers"
    ) => {
      return scheduledUpdates.some((update) => update[field]);
    },
    []
  );

  return {
    actions: {
      onUpdateScheduledUpdate,
      onDeleteScheduledUpdate,
      onCreateScheduledUpdate,
      onAddPerformer,
      updatePerformers,
    },
    state: {
      isEditingModalOpen,
      setIsEditingModalOpen,
      editingScheduledUpdate,
      setEditingScheduledUpdate,
      editingField,
      setEditingField,
      loading,
      hasScheduledUpdatesForField,
      getScheduledUpdateForField,
    },
  };
};

export default useScheduledUpdates;
